<template>
  <div class="pp">
    <div class="pp-search">
      <i class="material-icons">search</i>
      <input v-model="search" :placeholder="$t('dash.overview.dashboards.search_page')" />
    </div>
    <div class="pp-tools">
      <div v-if="networks.length > 1" class="pp-networks">
        <button v-for="n in networks" :key="n" class="ov-pp-net" :class="{ active: network === n }" @click="network = network === n ? null : n">
          {{ $t(`dash.overview.network.${n}`) }}
        </button>
      </div>
      <span class="pp-actions">
        <button class="ov-arrow-link" @click="selectVisible(true)">{{ $t('dash.overview.dashboards.select_all') }}</button>
        <button class="ov-arrow-link" @click="selectVisible(false)">{{ $t('dash.overview.dashboards.select_none') }}</button>
      </span>
    </div>

    <!-- Pages rangées par groupes du client (comme SelectPage) ; une case de groupe coche toutes ses pages -->
    <div v-for="group in groups" :key="group.key" class="pp-group">
      <div v-if="group.name" class="pp-group-head" @click="toggleGroup(group)">
        <span class="pp-check" :class="{ checked: groupState(group) === 'all', partial: groupState(group) === 'partial' }">
          <i v-if="groupState(group) !== 'none'" class="material-icons">{{ groupState(group) === 'all' ? 'check' : 'remove' }}</i>
        </span>
        <span class="pp-group-name">{{ group.name }}</span>
        <small>{{ group.pages.length }}</small>
        <button class="ov-pp-caret" @click.stop="toggleOpen(group)">
          <i class="material-icons">{{ isOpen(group) ? 'expand_less' : 'expand_more' }}</i>
        </button>
      </div>
      <ul v-if="!group.name || isOpen(group)" class="pp-pages" :class="{ 'pp-pages--nested': group.name }">
        <li v-for="page in group.pages" :key="`${group.key}-${page.id}`" class="pp-page" @click="togglePage(page)">
          <span class="pp-check" :class="{ checked: isSelected(page) }"><i v-if="isSelected(page)" class="material-icons">check</i></span>
          <img v-if="page.avatar" :src="page.avatar" class="pp-avatar" alt="" loading="lazy" referrerpolicy="no-referrer" @error="$event.target.style.visibility = 'hidden'" />
          <span v-else class="pp-avatar pp-avatar--empty">{{ (page.name || '?').charAt(0) }}</span>
          <span class="pp-page-name">{{ page.name }}</span>
          <Tooltip v-if="!isEligible(page)" :text="$t('dash.overview.dashboards.out_of_reporting_help')" position="left">
            <span class="pp-out">{{ $t('dash.overview.dashboards.out_of_reporting') }}</span>
          </Tooltip>
          <small>{{ $t(`dash.overview.network.${page.network}`) }}</small>
        </li>
      </ul>
    </div>
    <p v-if="!groups.length" class="pp-empty">{{ $t('dash.overview.dashboards.no_page_found') }}</p>
  </div>
</template>

<script>
import Tooltip from '../../tooltip/tooltipInit.vue'
import { isReportingEligible } from '@/utils/reportingEligibility'
import { groupPages } from './pageGroups'

/**
 * Sélection de pages par groupes du client, avec recherche et filtre réseau (v-model : ids en chaînes)
 */
export default {
  name: 'PagePicker',

  components: { Tooltip },

  props: {
    pages: { type: Array, default: () => [] },
    value: { type: Array, default: () => [] }
  },

  data() {
    return {
      search: '',
      network: null,
      // Groupes repliés (ouverts par défaut)
      closed: {}
    }
  },

  computed: {
    selected() {
      return (this.value || []).map(String)
    },

    networks() {
      return [...new Set(this.pages.map(p => p.network).filter(Boolean))]
    },

    visiblePages() {
      const q = this.search.trim().toLowerCase()
      return this.pages.filter(p => (!this.network || p.network === this.network) && (!q || (p.name || '').toLowerCase().includes(q)))
    },

    groups() {
      return groupPages(this.$store, this.visiblePages, this.$t('dash.overview.groups.others'))
    }
  },

  methods: {
    isEligible(page) {
      return isReportingEligible(page)
    },

    isSelected(page) {
      return this.selected.includes(String(page.id))
    },

    togglePage(page) {
      const id = String(page.id)
      this.$emit('input', this.isSelected(page) ? this.selected.filter(p => p !== id) : [...this.selected, id])
    },

    groupState(group) {
      const count = group.pages.filter(p => this.isSelected(p)).length
      return !count ? 'none' : count === group.pages.length ? 'all' : 'partial'
    },

    toggleGroup(group) {
      const ids = group.pages.map(p => String(p.id))
      const all = this.groupState(group) === 'all'
      this.$emit('input', all ? this.selected.filter(id => !ids.includes(id)) : [...new Set([...this.selected, ...ids])])
    },

    // Recherche active : groupes toujours dépliés
    isOpen(group) {
      return !!this.search.trim() || !this.closed[group.key]
    },

    toggleOpen(group) {
      this.$set(this.closed, group.key, this.isOpen(group))
    },

    selectVisible(select) {
      const ids = this.visiblePages.map(p => String(p.id))
      this.$emit('input', select ? [...new Set([...this.selected, ...ids])] : this.selected.filter(id => !ids.includes(id)))
    }
  }
}
</script>

<style scoped>
.pp-search {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  padding: 0 10px;
  border: 1.5px solid transparent;
  border-radius: 8px;
  background: #f6f1fa;
  transition: border-color 0.15s, background 0.15s;
}
.pp-search:focus-within {
  border-color: #e0d0ea;
  background: #fff;
}
.pp-search .material-icons {
  font-size: 17px;
  color: #9ba3b7;
}
.pp-search input {
  flex: 1;
  min-width: 0;
  height: 32px;
  border: none;
  outline: none;
  font-size: 13px;
  background: transparent;
}
.pp-tools {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
  margin-bottom: 6px;
}
.pp-networks {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}
.section-overview.ov.ov button.ov-pp-net {
  padding: 3px 9px;
  border: 1px solid #e0d0ea;
  border-radius: 20px;
  color: #6b1f4d;
  font-size: 11.5px !important;
}
.section-overview.ov.ov button.ov-pp-net.active {
  background: #6b1f4d;
  color: #fff;
}
.pp-actions {
  display: flex;
  gap: 10px;
  margin-left: auto;
  font-size: 12px;
}
.pp-group + .pp-group {
  margin-top: 4px;
}
.pp-group-head,
.pp-page {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 4px;
  border-radius: 6px;
  font-size: 12.5px;
  color: #121826;
  cursor: pointer;
}
.pp-group-head:hover,
.pp-page:hover {
  background: #f6f1fa;
}
.pp-group-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pp-group-head small,
.pp-page small {
  font-size: 11px;
  color: #9ba3b7;
}
.section-overview.ov.ov button.ov-pp-caret {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px !important;
  border-radius: 6px;
  color: #9ba3b7;
}
.section-overview.ov.ov button.ov-pp-caret:hover {
  background: #f1edf5;
  color: #6b1f4d;
}
.ov-pp-caret .material-icons {
  font-size: 18px;
}
.pp-pages {
  margin: 0;
  padding: 0;
  list-style: none;
}
.pp-pages--nested {
  margin-left: 12px;
  padding-left: 8px;
  border-left: 1px solid #f1edf5;
}
.pp-page-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pp-out {
  flex: 0 0 auto;
  padding: 1px 6px;
  border-radius: 8px;
  background: #f1f2f5;
  color: #9ba3b7;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
}
.pp-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  border: 1.5px solid #c9b8d4;
  border-radius: 4px;
  background: #fff;
}
.pp-check.checked {
  border-color: #6b1f4d;
  background: #6b1f4d;
}
.pp-check.partial {
  border-color: #6b1f4d;
}
.pp-check .material-icons {
  font-size: 13px;
  color: #fff;
}
.pp-check.partial .material-icons {
  color: #6b1f4d;
}
.pp-avatar {
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  object-fit: cover;
}
.pp-avatar--empty {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #e0d0ea;
  color: #6b1f4d;
  font-size: 10px;
  font-weight: 700;
}
.pp-empty {
  margin: 6px 0;
  font-size: 12px;
  color: #9ba3b7;
}
</style>
